import {HttpSvcFactory} from './HttpSvc';
import {Strings} from 'Strings';

export abstract class AnimatronSvc<T> {
    $rootScope: ng.IRootScopeService;
    $log: ng.ILogService;
    $timeout: ng.ITimeoutService;
    $q: ng.IQService;
    $stateParams: any;
    HttpSvc: HttpSvcFactory;

    httpGet: (config?: ng.IRequestShortcutConfig) => ng.IHttpPromise<anm.SearchResult<T>>;
    retries: number     = 0;
    retriesMax: number  = 120;
    results: T[]        = [];
    pages: T[][]        = [];
    busy: boolean       = false;
    currentPage: number = 0;
    count: number       = 0;
    totalPages: number  = 0;
    shownAll: boolean   = false;
    headers: {};
    loaded: boolean     = false;
    owner: boolean;
    protected foreignAccess: boolean;
    apiUnavaliable: boolean;
    uniqueId: string;
    seoPage             = +this.$stateParams.page;

    protected query: ProjectSearchCriteria = { //actually 'or userSearchCriteria' which doesn't exist
        product: 'STUDIO',
        offset: 0,
        limit: 10
    };

    constructor(query: ProjectSearchCriteria = {},
                secure = false,
                api = '',
                protected callback = angular.noop) {
        this.setSecure(secure, api);
        this.modifyQuery(query);
        if (this.onCreate) this.onCreate();

        this.addSeoPagination();
    }

    /*abstract*/
    protected onCreate(): void {
    }

    /*abstract*/
    protected decorate(results: Array<T>): void {
    }

    protected abstract makeHttpSvc(secure: boolean, api: string): (config?: ng.IRequestShortcutConfig) => ng.IHttpPromise<anm.SearchResult<T>>;

    protected setSecure(secure: boolean, api = '') {
        if (secure && Strings.isBlank(options.headers['Animatron-Security-Token'])) {
            if (console.trace) console.trace();
            throw new Error('Empty cookie');
        }
        this.owner   = secure;
        this.httpGet = this.makeHttpSvc(secure, api);
    }

    protected modifyQuery(query: ProjectSearchCriteria) {
        if (!_.isObject(query)) throw new Error('incorrect query');
        if (!this.owner && this.foreignAccess) this.query.scope = 'FOREIGN';
        _.extend(this.query, query);
    }

    reset(): void {
        this.busy = this.shownAll = false;
        this.currentPage = this.count = 0;
    }

    getResultsOnPage(pageNum: number = this.currentPage) {
        return this.pages[pageNum];
    }

    nextPage(shuffle = false): ng.IPromise<{count: number, results: Array<T>}> {
        if (this.busy || this.shownAll || (this.seoPage && this.results.length > 0)) return this.$q.reject();
        return this.loadPage(++this.currentPage).then(result => {
            if (shuffle) this.results = _.shuffle<T>(this.results);
            const lastPage = _.last(this.pages);
            if (lastPage && lastPage.length > 0) this.callback(lastPage);
            return result;
        });
    }

    reorderPages() {
        if (this.pages.length === 0) return;

        let prevIndex: number;
        let pageNumber       = 1;
        const pages: T[][]   = [];
        const itemsList: T[] = [];

        this.pages.forEach((items: T[], index: number) => {
            if (!prevIndex) {
                prevIndex = index;
            }

            if (!_.isEmpty(items) && prevIndex++ === index) {
                itemsList.push(...items);
            }
        });

        itemsList.forEach((item: T) => {
            if (pages[pageNumber] && pages[pageNumber].length >= this.query.limit) {
                pageNumber++;
            }

            if (!pages[pageNumber]) {
                pages[pageNumber] = [];
            }

            pages[pageNumber].push(item);
        });

        let lastPageList = pages[pages.length - 1];
        if (lastPageList.length !== this.query.limit && !this.shownAll) {
            pages.pop();
        }

        this.totalPages = this.getTotalPages();
        this.pages      = pages;
    }

    changeLimit(limit: number) {
        this.query.limit = limit;
        this.reorderPages();
    }

    loadPage(pageNum: number = this.currentPage): ng.IPromise<anm.SearchResult<T>> {
        if (!this.httpGet) return this.$q.reject('no httpGet');
        const resultsOnPage = this.getResultsOnPage(pageNum);
        if (resultsOnPage) {
            this.busy = false;
            return this.$q.resolve({count: this.count, results: resultsOnPage});
        }
        this.busy = true;

        const pgNm   = this.results.length === 0 ? this.seoPage || pageNum : pageNum;
        const params = _.extend({}, this.query, {
            offset: (pgNm - 1) * this.query.limit || 0
        });

        return this.httpGet({
            params,
            headers: this.headers
        }).then(r => r.data).then(data => {
            this.loaded = true;
            if (this.decorate) this.decorate(data.results);
            this.pages[pageNum] = data.results;
            this.flattenResults();

            this.count      = data.count || this.results.length;
            this.totalPages = this.getTotalPages();

            this.shownAll = (!this.query.noCount && this.count <= this.results.length) || data.results.length === 0;

            if (this.shownAll) {
                this.$rootScope.$emit('$projects:shownAll', this);
            }

            this.apiUnavaliable = this.busy = false;
            return data;
        }, response => {
            const {data, status} = response;
            this.loaded          = true;
            if (status <= 0) {
                this.apiUnavaliable = true;
                this.$log.error('Error occurred getting results: ' + data + ',status: ' + status);
                return this.retry(pageNum);
            } else {
                const message = data.message || 'Something went completely wrong';
                _.notification.showError(message);
                return Promise.reject(message);
            }
        });
    }

    private getTotalPages(): number {
        return Math.abs(Math.ceil(this.count / this.query.limit));
    }

    private flattenResults(): void {
        const flatResults: T[] = [];
        for (let i = 1; i < this.pages.length; i++) {
            const page = this.pages[i];
            if (!page) break;
            page.forEach(prj => flatResults.push(prj));
        }
        if (this.uniqueId) {
            this.results = _.unique(flatResults, false, item => item[this.uniqueId]);
        } else {
            this.results = flatResults;
        }
    }

    private retry(pageNum: number): ng.IPromise<any> {
        const deferred = this.$q.defer();

        if (this.retries < this.retriesMax) {
            const timeoutId = this.$timeout(() => {
                this.$log.debug(`${this.retries} retry out of ${this.retriesMax}`);
                this.retries += 1;
                this.busy = false;
                deferred.resolve(this.loadPage(pageNum));
            }, 1000);
            this.$rootScope.$on('$stateChangeStart', () => this.$timeout.cancel(timeoutId));
        } else {
            const message = `retry was called over ${this.retriesMax}`;
            this.$log.warn(message);
            deferred.reject(message);
        }

        return deferred.promise;
    }

    private addSeoPagination(): void {
        if (this.$stateParams.hasOwnProperty('page')) {
            const mkLink  = (rel: string | number) => {
                return document.head.querySelector(`[rel=${rel}]`) ||
                    document.head.appendChild(document.createElement('link'));
            };
            const pageNum = this.seoPage || 1;
            [['prev', pageNum - 1], ['next', pageNum + 1]]
                .filter(([_, n]) => n > 0)
                .forEach(([rel, n]) => {
                    const source = {rel, href: `${location.pathname}?page=${n}`};
                    const target = mkLink(rel);
                    Object.assign(target, source);
                });
        }
    }
}



// WEBPACK FOOTER //
// ./app/assets/javascripts/services/AnimatronSvc.ts